import { useMemo, useState } from "react";
import { Drawer, Input, Button, Tooltip } from "antd";
import { norm } from "../../utils/fuzzyMatch";

type PropertyOption = { id: string; name: string; color?: string | null };

const PALETTE = [
  "#EF4444",
  "#F97316",
  "#F59E0B",
  "#EAB308",
  "#84CC16",
  "#22C55E",
  "#10B981",
  "#14B8A6",
  "#06B6D4",
  "#0EA5E9",
  "#3B82F6",
  "#6366F1",
  "#8B5CF6",
  "#A855F7",
  "#D946EF",
  "#EC4899",
  "#F43F5E",
  "#64748B",
  "#6B7280",
  "#9CA3AF",
  "#111827",
  "#2B4C7E",
  "#47BEF5",
  "#6DD3FF",
];

export function ManageColor({
  open,
  onClose,
  propiedades,
  onUpdateColor,
  onAutoAssign,
}: {
  open: boolean;
  onClose: () => void;
  propiedades: PropertyOption[];
  onUpdateColor: (id: string, color: string | null) => void;
  onAutoAssign: () => void;
}) {
  const [q, setQ] = useState("");
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const filtered = useMemo(() => {
    // norm() pliega tildes ("concon" encuentra "Concón") — aud. 2026-07-13.
    const qq = norm(q);
    if (!qq) return propiedades;
    return propiedades.filter((p) => norm(p.name).includes(qq));
  }, [q, propiedades]);

  const selected = useMemo(
    () => propiedades.find((p) => p.id === selectedId) ?? null,
    [selectedId, propiedades]
  );

  return (
    <Drawer
      title="Administrar propiedades"
      placement="right"
      width={520}
      open={open}
      onClose={onClose}
      destroyOnClose
    >
      <div style={{ display: "flex", gap: 8, marginBottom: 10 }}>
        <Input
          placeholder="Buscar propiedad..."
          value={q}
          onChange={(e) => setQ(e.target.value)}
          allowClear
        />
        <Tooltip title="Asigna colores automáticamente usando una paleta consistente">
          <Button onClick={onAutoAssign}>Auto</Button>
        </Tooltip>
      </div>

      <div
        style={{ display: "grid", gridTemplateColumns: "1fr 190px", gap: 12 }}
      >
        {/* Lista virtualizada */}
        <div style={{ border: "1px solid rgba(0,0,0,0.06)", borderRadius: 12 }}>
          <div style={{ maxHeight: 520, overflow: "auto" }}>
            {filtered.map((p) => {
              const isActive = p.id === selectedId;
              return (
                <div
                  key={p.id}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                    padding: "0 12px",
                    height: 48,
                    cursor: "pointer",
                    background: isActive
                      ? "rgba(71,190,245,0.10)"
                      : "transparent",
                  }}
                  onClick={() => setSelectedId(p.id)}
                >
                  <span
                    style={{
                      width: 12,
                      height: 12,
                      borderRadius: 999,
                      background: p.color || "rgba(0,0,0,0.2)",
                      border: "1px solid rgba(0,0,0,0.08)",
                      flex: "0 0 auto",
                    }}
                  />
                  <div
                    style={{
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {p.name}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Paleta */}
        <div>
          <div style={{ fontWeight: 700, marginBottom: 8 }}>Color</div>
          <div style={{ marginBottom: 10, color: "rgba(0,0,0,0.55)" }}>
            {selected ? selected.name : "Selecciona una propiedad"}
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(6, 1fr)",
              gap: 8,
            }}
          >
            {PALETTE.map((c) => (
              <button
                key={c}
                disabled={!selected}
                onClick={() => selected && onUpdateColor(selected.id, c)}
                style={{
                  width: 28,
                  height: 28,
                  borderRadius: 10,
                  border: "1px solid rgba(0,0,0,0.10)",
                  background: c,
                  cursor: selected ? "pointer" : "not-allowed",
                }}
              />
            ))}
          </div>

          <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
            <Button
              disabled={!selected}
              onClick={() => selected && onUpdateColor(selected.id, null)}
            >
              Quitar color
            </Button>
          </div>
        </div>
      </div>
    </Drawer>
  );
}
